import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImagePlus, Loader2, FileText } from "lucide-react";
import { RES_STATUSES } from "./shared";

export function DispatchTab({ reservations, vehicles, drivers, assign, setResStatus, readOnly }) {
  return (
    <div className="rounded-lg border border-white/10 bg-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="dispatch-table">
          <thead>
            <tr className="border-b border-white/10 text-left text-muted-foreground text-xs uppercase tracking-luxe">
              <th className="px-4 py-4">Ref</th>
              <th className="px-4 py-4">Client</th>
              <th className="px-4 py-4">Pickup</th>
              <th className="px-4 py-4">Vehicle</th>
              <th className="px-4 py-4">Driver</th>
              <th className="px-4 py-4">Status</th>
              <th className="px-4 py-4">Sheet</th>
            </tr>
          </thead>
          <tbody>
            {reservations.map((r) => (
              <tr key={r.id} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`dispatch-row-${r.id}`}>
                <td className="px-4 py-4 text-white/80 text-xs">{r.reservation_number || r.id}</td>
                <td className="px-4 py-4"><p className="text-white">{r.name}</p><p className="text-muted-foreground text-xs">{r.service_label}</p></td>
                <td className="px-4 py-4 text-white/80">{r.date}<br /><span className="text-muted-foreground text-xs">{r.time}</span></td>
                <td className="px-4 py-4">
                  <Select value={r.vehicle_id || ""} onValueChange={(v) => assign(r.id, "vehicle_id", v)} disabled={readOnly}>
                    <SelectTrigger data-testid={`assign-vehicle-${r.id}`} className="h-9 w-40 bg-secondary border-white/10 text-white text-xs disabled:opacity-60"><SelectValue placeholder="Assign vehicle" /></SelectTrigger>
                    <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-4">
                  <Select value={r.driver_id || ""} onValueChange={(v) => assign(r.id, "driver_id", v)} disabled={readOnly}>
                    <SelectTrigger data-testid={`assign-driver-${r.id}`} className="h-9 w-36 bg-secondary border-white/10 text-white text-xs disabled:opacity-60"><SelectValue placeholder="Assign driver" /></SelectTrigger>
                    <SelectContent>{drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-4">
                  <Select value={r.status} onValueChange={(v) => setResStatus(r.id, v)} disabled={readOnly}>
                    <SelectTrigger data-testid={`res-status-${r.id}`} className="h-9 w-40 bg-secondary border-white/10 text-white text-xs disabled:opacity-60"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {RES_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-4">
                  <a href={`/admin/trip-sheet/${r.id}`} target="_blank" rel="noopener noreferrer"
                    data-testid={`trip-sheet-link-${r.id}`}
                    className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-luxe px-3 py-1.5 rounded-full border border-white/20 text-white hover:bg-white hover:text-black transition-colors">
                    <FileText className="h-3.5 w-3.5" /> Sheet
                  </a>
                </td>
              </tr>
            ))}
            {reservations.length === 0 && <tr><td colSpan={7} className="px-5 py-16 text-center text-muted-foreground">No reservations yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function FleetTab({ vehicles, toggleVehicle, uploadPhoto, uploadingId, backendUrl, readOnly }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="fleet-grid">
      {vehicles.map((v) => (
        <div key={v.id} className="rounded-lg border border-white/10 bg-card overflow-hidden" data-testid={`fleet-card-${v.id}`}>
          <div className="relative aspect-[16/10] bg-secondary/50 border-b border-white/10 group">
            {v.photo ? (
              <img src={`${backendUrl}${v.photo}`} alt={v.name} className="w-full h-full object-cover" data-testid={`vehicle-photo-${v.id}`} />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground gap-2">
                <ImagePlus className="h-7 w-7" />
                <span className="text-xs">No photo yet</span>
              </div>
            )}
            <label className="absolute bottom-3 right-3 cursor-pointer" data-testid={`upload-photo-label-${v.id}`} hidden={readOnly}>
              <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
                data-testid={`upload-photo-input-${v.id}`}
                onChange={(e) => uploadPhoto(v.id, e)} disabled={uploadingId === v.id || readOnly} />
              <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-luxe px-3 py-1.5 rounded-full bg-black/70 backdrop-blur border border-white/20 text-white hover:bg-white hover:text-black transition-colors">
                {uploadingId === v.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
                {v.photo ? "Change" : "Add photo"}
              </span>
            </label>
          </div>
          <div className="p-6">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-serif text-xl text-white">{v.name}</h3>
                <p className="text-muted-foreground text-xs">{v.vehicle_class} · Unit {v.unit_number}</p>
              </div>
              <button onClick={() => toggleVehicle(v)} data-testid={`toggle-vehicle-${v.id}`} disabled={readOnly}
                className={`text-[10px] uppercase tracking-luxe px-2.5 py-1 rounded-full border disabled:opacity-60 disabled:cursor-default ${v.active ? "text-white border-white/40 bg-white/10" : "text-muted-foreground border-white/15"}`}>
                {v.active ? "Active" : "Inactive"}
              </button>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground mb-3">
              <span>{v.capacity} pax</span><span>{v.luggage_capacity} bags</span>
              <span>{v.quote_only ? "Quote-only" : "Instant"}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(v.amenities || []).map((a) => <span key={a} className="text-[10px] text-white/60 border border-white/10 rounded-full px-2 py-0.5">{a}</span>)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
